import React, { useState, useEffect } from 'react';
import { sendContactForm } from '../services/emailService';
import '../styles/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Check for project info on component mount
  useEffect(() => {
    const projectInfoStr = sessionStorage.getItem('contactProjectInfo');
    if (projectInfoStr) {
      try {
        const projectInfo = JSON.parse(projectInfoStr);
        setFormData(prev => ({
          ...prev,
          subject: `Tư vấn về dự án: ${projectInfo.title}`,
          message: `Xin chào,\n\nTôi quan tâm đến dự án "${projectInfo.title}" trong danh mục ${projectInfo.category}. Vui lòng liên hệ với tôi để được tư vấn chi tiết về:\n\n- Phạm vi dự án và yêu cầu kỹ thuật\n- Thời gian thực hiện\n- Chi phí ước tính\n- Quy trình triển khai\n\nCảm ơn!`
        }));
        
        // Clear the session storage after using
        sessionStorage.removeItem('contactProjectInfo');
      } catch (error) {
        console.warn('Could not parse project info from session storage:', error);
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear submit status when user starts typing again
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Validate required fields
      if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
        throw new Error('Vui lòng điền đầy đủ thông tin bắt buộc');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Email không hợp lệ');
      }

      // Validate message length
      if (formData.message.length < 10) {
        throw new Error('Tin nhắn quá ngắn. Vui lòng viết ít nhất 10 ký tự.');
      }

      if (formData.message.length > 5000) {
        throw new Error('Tin nhắn quá dài. Vui lòng viết dưới 5000 ký tự.');
      }

      console.log('📧 Sending contact form via EmailJS...');
      
      // Send email via EmailJS
      const result = await sendContactForm(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        // Auto clear success message after 8 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 8000);
      } else {
        throw new Error(result.message || 'Có lỗi xảy ra khi gửi email');
      }

    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      
      // Handle different types of errors
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Có lỗi xảy ra. Vui lòng thử lại sau.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '✉️',
      title: 'Email Address',
      details: ['info@phucansolutions.vn', 'support@phucansolutions.vn'],
      bgColor: 'contact-card-blue'
    },
    {
      icon: '📞',
      title: 'Phone Number',
      details: ['📞 0918 93 11 93', '📞 0918 28 11 93','📞 0939 591 599'],
      bgColor: 'contact-card-blue'
    },
    {
      icon: '📍',
      title: 'Office & Showroom Locations',
      details: ['351 Nguyễn Huệ, Bình Long, Đồng Nai'],
      bgColor: 'contact-card-blue'
    },
    {
      icon: '🕒',
      title: 'Work Day',
      details: ['Mon - Fri: 07:30 - 17:30', 'Sat: 07:30 - 11:30'],
      bgColor: 'contact-card-blue'
    }
  ];

  return (
    <main className="contact-main">
      {/* Hero Section */}
      <div className="contact-hero">
        <div className="contact-hero-content">
          <h1>Contact Us</h1>
          <p className="hero-subtitle">Chúng tôi luôn sẵn sàng lắng nghe bạn.</p>
          <p className="hero-description">
            Dù là thắc mắc nhỏ hay ý tưởng lớn, đừng ngần ngại chia sẻ với chúng tôi. Mọi phản hồi của 
            bạn là cơ hội để chúng tôi phục vụ tốt hơn.
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="contact-info-section">
        <div className="contact-container">
          <div className="contact-info-grid">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact-info-card">
                <div className={`contact-card-header ${info.bgColor}`}>
                  <div className="contact-icon">
                    <span>{info.icon}</span>
                  </div>
                  <h3>{info.title}</h3>
                </div>
                <div className="contact-card-content">
                  {info.details.map((detail, idx) => (
                    <p key={idx}>{detail}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="contact-form-section">
        <div className="contact-container">
          <div className="contact-form-header">
            <span className="section-label">CONTACT</span>
            <h2>Get In Touch With Us</h2>
            <p>
              Chúng tôi cung cấp các giải pháp linh hoạt cho nhiều lĩnh vực. Nếu bạn đang tìm 
              kiếm đối tác đáng tin cậy, đừng ngần ngại liên hệ – chúng tôi luôn sẵn sàng đồng 
              hành cùng bạn.
            </p>
          </div>

          <div className="contact-form-grid">
            {/* Contact Form */}
            <div className="form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-row">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <textarea
                  name="message"
                  placeholder="Message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                ></textarea>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                >
                  {isSubmitting ? (
                    <div className="loading-content">
                      <div className="spinner"></div>
                      Đang gửi...
                    </div>
                  ) : (
                    'SEND MESSAGE'
                  )}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="status-message success">
                    <span>✅</span>
                    <p>Cảm ơn bạn đã liên hệ! Email đã được gửi thành công. Chúng tôi sẽ phản hồi trong vòng 24 giờ.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="status-message error">
                    <span>❌</span>
                    <div>
                      <p>{errorMessage}</p>
                      <p className="error-help">
                        Bạn có thể liên hệ trực tiếp qua email: info@phucansolutions.vn hoặc điện thoại: 0918 93 11 93
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Illustration */}
            <div className="illustration-container">
              <img 
                src="/images/contact.png" 
                alt="Contact Illustration" 
                className="contact-illustration"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="contact-map-section">
        <div className="contact-container">
          <div className="map-header">
            <h2>Vị trí văn phòng</h2>
            <p>Hãy đến thăm chúng tôi tại văn phòng</p>
          </div>
          
          <div className="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.5238914389342!2d106.60733137689354!3d11.657236588550193!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3173541240b35de1%3A0x149d05ddc99b8980!2zMzUxIE5ndXnhu4VuIEh14buHLCBBbiBM4buZYywgQsOsbmggTG9uZywgQsOsbmggUGjGsOG7m2MgNjc4NTAsIFZp4buHdCBOYW0!5e0!3m2!1svi!2s!4v1748691514682!5m2!1svi!2s" 
              width="100%" 
              height="600" 
              style={{border: 0}} 
              allowFullScreen={true}
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              className="map-iframe"
              title="Office Location"
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default Contact; 